import {
  CATEGORY_PAGE_REFERRER,
  HAS_SIMPLEX_PAGE_META_TAG,
  SIMPLEX_PAGE_REFERRER
} from '../../utils/constants'
import { IS_ON_MOBILE_APP } from '../../constants'

interface tagType {
  type: string
  label: string
}

type RegionalAttributesType = {
  promotionalPrice: number
  originalPrice: number
  suggestedPrice: number
  available: boolean
  availableForEcommerce: boolean
  offer: {
    enabled: boolean
    type: string
    label: string
    value: number
  }
  installments: {
    amount: number
    value: string
    totalPrice: string
  }
  discount: {
    discountType: string
    paymentMethodDiscount: string
    featureInstallmentDiscount: string
  }
  facets: {
    blackFriday: boolean
    freeShipping: boolean
    expressShipping: boolean
    coupon: boolean
  }
}

interface ProductProps {
  objectID: string
  name: string
  shortName: string
  url: string
  relevanceFields: {
    [key: string]: {
      relevance: number
    }
  }
  attributes: {
    [key: string]: string
  }
  categoryPageId: string
  regionalAttributes: {
    [key: string]: RegionalAttributesType
  }
  pictures: {
    micro: string
    normal: string
  }
  unit: string
}

export const normalize = (
  product: ProductProps,
  region: string,
  isCategoryPage = false
) => {
  const REGIONAL_DATA = product.regionalAttributes?.[region]

  const getOffer = () => {
    if (!REGIONAL_DATA?.offer?.enabled) {
      return null
    }

    return {
      type: REGIONAL_DATA?.offer?.type,
      label: REGIONAL_DATA?.offer?.label,
      value: REGIONAL_DATA?.offer?.value
    }
  }

  const getTag = () => {
    if (REGIONAL_DATA?.facets?.blackFriday) {
      return {
        type: 'outlet',
        label: 'Black Friday'
      }
    }

    if (REGIONAL_DATA?.facets?.freeShipping) {
      return {
        type: 'freeShipping',
        label: 'Frete Grátis'
      }
    }

    if (REGIONAL_DATA?.facets?.coupon && !IS_ON_MOBILE_APP) {
      return {
        type: 'coupon',
        label: 'Cupom Disponível'
      }
    }

    if (product.relevanceFields?.isOurLowestPrice) {
      return {
        type: 'lowestprice',
        label: 'Nosso preço + baixo'
      }
    }

    if (product.relevanceFields?.novelty) {
      return {
        type: 'news',
        label: 'NOVIDADE'
      }
    }

    return null
  }

  const getPrice = (value: number) => {
    if (!value) {
      return null
    }

    const splitedPrice = String(value.toFixed(2)).split('.')

    return {
      integers: Number(splitedPrice[0]).toLocaleString(),
      decimals: splitedPrice[1]
    }
  }

  const isOutlet = (tag: tagType) => {
    return tag.type === 'outlet'
  }

  const tag = getTag()
  const hasOutlet = Boolean(tag && isOutlet(tag))

  const getInstallments = (
    installments: RegionalAttributesType['installments']
  ) => {
    if (Array.isArray(installments)) {
      return null
    }

    return installments
  }

  const getDiscount = (discount: RegionalAttributesType['discount']) => {
    if (!discount || Array.isArray(discount)) {
      return {
        discountType: '',
        paymentMethodsDiscount: '',
        featureInstallmentDiscount: ''
      }
    }

    return discount
  }

  const getUrl = (url: string) => {
    const separator = url?.includes('?') ? '&' : '?'

    if (isCategoryPage) {
      return `${url}${separator}referrer=${CATEGORY_PAGE_REFERRER}`
    }

    if (HAS_SIMPLEX_PAGE_META_TAG) {
      return `${url}${separator}referrer=${SIMPLEX_PAGE_REFERRER}`
    }

    return url
  }

  return {
    id: product.objectID,
    shortName: product.shortName || product.name,
    picture: product.pictures?.normal,
    unit: product.unit,
    inWishList: false,
    installments: getInstallments(REGIONAL_DATA?.installments),
    isOutlet: hasOutlet,
    tag,
    offer: getOffer(),
    price: {
      to: getPrice(REGIONAL_DATA?.promotionalPrice),
      from: getPrice(REGIONAL_DATA?.originalPrice),
      regionPrice: null,
      suggested: getPrice(REGIONAL_DATA?.suggestedPrice)
    },
    discount: getDiscount(REGIONAL_DATA?.discount),
    url: getUrl(product.url),
    trackingUrl: null,
    assistedSale: false,
    isSoldOut: !REGIONAL_DATA?.available,
    isExpressDelivery: false,
    expressDeliveryDay: '',
    name: product.name,
    services: false,
    brand: product?.attributes?.Marca ?? '',
    categoryTree: product?.categoryPageId
  }
}

type StoreData = {
  location: string
  promotionalPrice: number
  originalPrice: number
  installments: {
    amount: number
    value: string
    totalPrice: string
  } | null
  isVisible: boolean
  isVisibleOnRegion: boolean
  offer: {
    enabled: boolean
    type: string
    label: string
    value: number
  }
  stock: {
    store: number
    warehouse: number
    storeAndWarehouse: number
    region: number
  }
  tags: Record<string, string>
  regionalFacets: {
    freeShipping: boolean
    expressShipping: boolean
    blackFriday: boolean
  }
}

export const normalizeAssistedSale = (
  product: Record<string, unknown>,
  storeSlug: string
) => {
  const storeData = product[`store_${storeSlug}`] as StoreData | undefined

  const getPrice = (value?: number) => {
    if (!value) return null
    const splitedPrice = String(value.toFixed(2)).split('.')
    return {
      integers: Number(splitedPrice[0]).toLocaleString(),
      decimals: splitedPrice[1]
    }
  }

  const getOffer = () => {
    if (!storeData?.offer?.enabled) return null
    return {
      type: storeData.offer.type,
      label: storeData.offer.label,
      value: storeData.offer.value
    }
  }

  const getTag = () => {
    if (storeData?.regionalFacets?.blackFriday) {
      return { type: 'outlet', label: 'Black Friday' }
    }
    if (storeData?.regionalFacets?.freeShipping) {
      return { type: 'freeShipping', label: 'Frete Grátis' }
    }
    return null
  }

  const tag = getTag()

  return {
    id: product.objectID as string,
    shortName: (product.shortName as string) || (product.name as string),
    picture: (product.pictures as Record<string, unknown>)?.normal,
    unit: product.unit as string,
    inWishList: false,
    installments: storeData?.installments ?? null,
    isOutlet: tag?.type === 'outlet',
    tag,
    offer: getOffer(),
    price: {
      to: getPrice(storeData?.promotionalPrice),
      from: getPrice(storeData?.originalPrice),
      regionPrice: null,
      suggested: null
    },
    discount: {
      discountType: '',
      paymentMethodsDiscount: '',
      featureInstallmentDiscount: ''
    },
    url: product.url as string,
    trackingUrl: null,
    assistedSale: storeData
      ? {
          tags: Object.values(storeData.tags || {}),
          stock: {
            store: storeData.stock?.store ?? 0,
            warehouse: storeData.stock?.warehouse ?? 0,
            region: storeData.stock?.region ?? 0
          }
        }
      : false,
    isSoldOut: !storeData?.isVisible,
    isExpressDelivery: false,
    expressDeliveryDay: '',
    name: product.name as string,
    services: false,
    brand:
      ((product.attributes as Record<string, unknown>)?.Marca as string) ?? '',
    categoryTree: product.categoryPageId as string[]
  }
}

export const getItemCategory = (categoryTree: string[]) => {
  if (!categoryTree.length) {
    return ''
  }

  const replaceAndTransform = (item: string) => {
    return item.replaceAll(' ', '-').toLowerCase()
  }

  if (categoryTree.length === 1) {
    return replaceAndTransform(categoryTree[0])
  }

  // Pega apenas os dois primeiros níveis da árvore
  return replaceAndTransform(categoryTree[1].split(' > ').join('|'))
}
